import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ClipboardList, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

const actionColors = {
  create: 'bg-[#ECFDF5] text-[#059669]',
  update: 'bg-[#EEF2FF] text-[#4F46E5]',
  delete: 'bg-[#FEF2F2] text-[#DC2626]',
  upload: 'bg-[#F0E6FF] text-[#7B2FBE]',
  login: 'bg-[#FFFBEB] text-[#D97706]',
  signup: 'bg-[#ECFDF5] text-[#059669]',
  google_login: 'bg-[#FFFBEB] text-[#D97706]',
};

export default function AuditTrailPage() {
  const { apiCall } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (entityFilter) params.set('entity_type', entityFilter);
      if (actionFilter) params.set('action', actionFilter);
      const res = await apiCall('get', `/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, entityFilter, actionFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div data-testid="audit-trail-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Audit Trail</h1>
        <p className="text-[#64748B] mb-8">Track all actions for compliance audits</p>
      </motion.div>

      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.05 }} className="flex items-center gap-4 mb-6">
        <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44 rounded-xl border-gray-200 h-11" data-testid="filter-entity-type">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="employee">Employee</SelectItem>
            <SelectItem value="paf">PAF</SelectItem>
            <SelectItem value="document">Document</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44 rounded-xl border-gray-200 h-11" data-testid="filter-action">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="upload">Upload</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-white rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="w-12 h-12 text-[#64748B] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#64748B]">No audit logs found</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-indigo-100/50">
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Timestamp</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">User</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Action</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Entity</TableHead>
                  <TableHead className="text-[#64748B] font-medium text-xs uppercase tracking-wider">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="border-b border-indigo-100/50">
                    <TableCell className="text-sm text-[#64748B]">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm text-[#0F172A] font-medium">{log.user_name || 'System'}</p>
                        <p className="text-xs text-[#64748B]">{log.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${actionColors[log.action] || 'bg-gray-100 text-gray-600'}`}>
                        {log.action}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-[#64748B] capitalize">{log.entity_type}</TableCell>
                    <TableCell className="text-sm text-[#64748B] max-w-xs truncate">{log.details || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > 30 && (
              <div className="flex items-center justify-between p-4 border-t border-indigo-100/50">
                <span className="text-sm text-[#64748B]">Showing {(page-1)*30+1}–{Math.min(page*30, total)} of {total}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p-1)} className="rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={page*30 >= total} onClick={() => setPage(p => p+1)} className="rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
